import { forwardRef, useRef, useState } from "react";

import type { TextInputProps } from "@ui/components/Inputs/TextInput";
import { TextInput } from "@ui/components/Inputs/TextInput";
import { useOutsideClick } from "@ui/hooks/useOutsideClick";
import { useTranslations } from "@ui/providers/useTranslations";

import { useConfig } from "@jti/app-context/useConfig";
import type { YandexSuggestion } from "@jti/rest-api";
import { YandexApi } from "@jti/rest-api";

import type { PlaceInputResults } from "../../PlaceInput";

import styles from "./YandexPlaceInput.module.scss";

type YandexPlaceInputProps = {
  onPlace?: (result: PlaceInputResults) => void;
} & TextInputProps;

const getValueByKind = (suggestion: YandexSuggestion, searchString: string): string => {
  return suggestion.address?.component?.find(({ kind }) => kind?.includes(searchString))?.name ?? "";
};

const prepareYandexAutocompleteResults = (suggestion: YandexSuggestion) => {
  return {
    city: getValueByKind(suggestion, "LOCALITY"),
    streetName: getValueByKind(suggestion, "STREET"),
    streetNumber: getValueByKind(suggestion, "HOUSE"),
    postcode: "",
    provinceName: "",
    districtName: "",
  };
};

const filterResultsByCountry = (results: YandexSuggestion[], countryName: string): YandexSuggestion[] => {
  return results.filter(
    ({ address }) =>
      address?.formatted_address &&
      address.component?.some(({ kind, name }) => kind?.includes("COUNTRY") && name === countryName)
  );
};

export const YandexPlaceInput = forwardRef<HTMLInputElement, YandexPlaceInputProps>(
  ({ onPlace, onChange, value, ...props }, ref) => {
    const { __ } = useTranslations();
    const [suggestions, setSuggestions] = useState<YandexSuggestion[]>([]);
    const dropDownContainerRef = useRef<HTMLDivElement>(null);

    const { COUNTRY_NAME } = useConfig();
    useOutsideClick(dropDownContainerRef, () => setSuggestions([]));
    const yandexApi = YandexApi.init();

    const fetchSuggestions = async (text: string) => {
      const results = await yandexApi.getSuggestions(text);
      if (!results.length) {
        setSuggestions([]);
        return;
      }
      setSuggestions(filterResultsByCountry(results, COUNTRY_NAME));
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      onChange?.(event);

      if (inputValue.length) {
        fetchSuggestions(inputValue);
      } else {
        setSuggestions([]);
      }
    };

    const handleSuggestionClick = (suggestion: YandexSuggestion) => {
      const address = prepareYandexAutocompleteResults(suggestion);
      setSuggestions([]);
      onPlace?.(address);
    };

    return (
      <div className={styles.yandexPlaceInputWrapper}>
        <TextInput
          {...props}
          aria-label={__("address_fields.street_name")}
          data-testid="yandex-place-input"
          autoComplete="off"
          value={value}
          onChange={handleInputChange}
          ref={ref}
        />
        {suggestions.length > 0 && (
          <div ref={dropDownContainerRef} className={styles.yandexDropdownContainer}>
            {suggestions.map((suggestion, index) => (
              <button
                type="button"
                key={`${suggestion.address?.formatted_address}-${index}`}
                onClick={() => handleSuggestionClick(suggestion)}
                className={styles.yandexDropdownItem}
              >
                {suggestion.address?.formatted_address}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
);
